import { SyntheticEvent, useEffect, useRef, useState } from "react";
import PortalGlassLightbox from "../PortalGlassLightbox";
import Icon from "../../icons/Icon";
import { IShowMaterialMediaByContentTypeProps } from "../../../properties/ActivityProperties";
import AudioPlayer from "../../../../components/learner/audio/AudioPlayer";

/** The player reports progress to its host; as an option there is no host to tell. */
const NOOP = () => {};

/** The player's own interactive parts — pressing one of these must not also answer the question. */
const PLAYER_CONTROL_SELECTOR = ".qbx-play-btn, .qbx-rate-btn, .qbx-slider";

/**
 * Stop an event ONLY when it came from a player control.
 *
 * An option carrying a recording is also the thing the learner picks, and in several templates the
 * select handler sits ABOVE this player rather than beside it: the fill-in-the-blanks bank selects
 * on the wrapper's onClick and drags from its parent's onMouseDown/onTouchStart, and the grouping
 * and matching chips are buttons that contain the full-width player. Stopping every event over the
 * player's whole area therefore swallowed the option's only select path — the clip became audible
 * but the option became close to unpickable, answerable only by hitting a few pixels of chip
 * padding.
 *
 * Scoping the stop to the controls keeps both: pressing play, changing the rate or dragging the
 * scrubber does not pick or drag the option, while a press anywhere else in the player's box
 * bubbles up and selects it exactly as a text option would.
 */
const stopOnlyFromPlayerControl = (event: SyntheticEvent<HTMLDivElement>) => {
  const target = event.target as HTMLElement | null;
  if (target?.closest?.(PLAYER_CONTROL_SELECTOR)) {
    event.stopPropagation();
  }
};

const ShowMaterialMediaByContentType = ({
  key,
  contentType,
  src,
  canFullScreen,
}: IShowMaterialMediaByContentTypeProps) => {
  const [showFullScreen, setShowFullScreen] = useState<boolean>(false);
  const [selectedFullScreenItem, setSelectedFullScreenItem] = useState<
    any | null
  >(null);
  const [isLoaded, setIsLoaded] = useState<boolean>(false);
  const imageRef = useRef<HTMLImageElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    setIsLoaded(false);
  }, []);

  // The enlarged image opens in the shared zoomable lightbox (wheel / pinch / drag / double-click,
  // −/+/reset, Esc and backdrop close) — the same viewer the MCSA options use.
  const RenderShowFullScreenItem = () => {
    if (!showFullScreen || !selectedFullScreenItem) return null;
    return (
      <PortalGlassLightbox
        src={selectedFullScreenItem}
        onClose={() => {
          setShowFullScreen(false);
          setSelectedFullScreenItem(null);
        }}
      />
    );
  };

  return contentType === "IMAGE" ? (
    <>
      {RenderShowFullScreenItem()}
      <div className="med-mwrap">
        <div className="med-mcenter">
          <div className="med-mbox-rel">
            {src ? (
              <img
                src={src}
                alt="image"
                ref={imageRef}
                className="med-w-mediabox med-h-mediabox med-img-contain-rounded"
                onLoad={() => {
                  setIsLoaded(true);
                }}
                style={{ objectFit: "contain" }}
              />
            ) : null}
            {src !== null &&
            src !== "" &&
            src !== "DEFAULT_OPTION" &&
            canFullScreen ? (
              <div
                className="med-fs-btn-bg"
                onMouseEnter={(e) => {
                  e.preventDefault();
                }}
                onClick={(e) => {
                  e.preventDefault();
                  // Stop the bubble: as an OPTION medium (MCMA image options) this
                  // sits inside a clickable option row, so zooming also toggled
                  // the answer. Zoom must inspect, never answer.
                  e.stopPropagation();
                  setShowFullScreen(true);
                  setSelectedFullScreenItem(src);
                }}
              >
                <Icon name="arrow-up" size={16} />
              </div>
            ) : null}
          </div>
        </div>
      </div>
    </>
  ) : contentType === "VIDEO" ? (
    <div className="med-mwrap">
      <div className="med-mcenter">
        <div className="med-mbox">
          <video
            ref={videoRef}
            className="med-video"
            src={src}
            controls
            onClick={() => {}}
          />
        </div>
      </div>
    </div>
  ) : contentType === "AUDIO" ? (
    // The learner AudioPlayer, the same one an assignment material / podcast uses:
    // play/pause, a scrubber with real elapsed time and a playback-rate control. A bare
    // <audio controls> was unusable as an OPTION - in GROUPING and ORDERING the option is
    // a DRAGGABLE chip, so pressing the native play button started a drag instead of
    // playing, and the clip could not be heard at all.
    //
    // Audio has no intrinsic height, so a fixed square media box would just waste vertical
    // space and make option rows huge; the wrapper stays full-width and auto-height.
    //
    // Events from the player's CONTROLS stop here, and only those: the player sits inside a
    // draggable chip and, for MCSA/MCMA, inside a clickable option row, so pressing play must not
    // drag or answer. Everything else in this box still bubbles, because for several templates the
    // option's only select path runs through it — see stopOnlyFromPlayerControl.
    <div
      className="med-audio-wrap"
      onPointerDown={stopOnlyFromPlayerControl}
      onMouseDown={stopOnlyFromPlayerControl}
      onTouchStart={stopOnlyFromPlayerControl}
      onClick={stopOnlyFromPlayerControl}
    >
      <AudioPlayer
        // Keyed on the source: the player owns its play/rate/elapsed state,
        // and swapping src underneath it left the previous clip's controls on screen.
        key={src}
        audioFileList={[{ url: src }]}
        currentAudioIndex={0}
        onAudioIndexChange={NOOP}
        onTimeUpdate={NOOP}
        onPlayStateChange={NOOP}
        isExternallyPaused={false}
        seekToMs={null}
      />
    </div>
  ) : null;
};

export default ShowMaterialMediaByContentType;
